import { api } from '../../api/client';

const BASE = '/api/v1/sql-console';

/** One row of a query result, keyed by a stable synthetic field id (col_0, col_1…)
 *  so duplicate column names (SELECT * across a join) never collide. */
export type ResultRow = Record<string, string | null>;

export interface ExecuteResult {
  succeeded: boolean;
  columns: string[];
  rows: (string | null)[][];
  rowsReturned: number;
  truncated: boolean;
  durationMs: number;
  dataSource: string;
  message?: string | null;
}

export interface SchemaColumn {
  name: string;
  dataType: string;
  isPrimaryKey: boolean;
}

export interface SchemaTable {
  schema: string;
  table: string;
  columns: SchemaColumn[];
}

/** Run a single read-only SELECT against the application's own database.
 *  The endpoint returns HTTP 200 even for a blocked/failed query — the failure
 *  is inside the payload (`succeeded === false` + `message`). */
export async function executeQuery(sql: string): Promise<ExecuteResult> {
  const res = await api.post<{ success: boolean; data: ExecuteResult }>(`${BASE}/execute`, { sql });
  return res.data.data;
}

/** One foreign-key column pair — drives the Query Builder's auto-join. */
export interface ForeignKey {
  fromSchema: string; fromTable: string; fromColumn: string;
  toSchema: string; toTable: string; toColumn: string;
}

export interface SchemaResult {
  tables: SchemaTable[];
  foreignKeys: ForeignKey[];
}

/** The app database's tables + columns (schema rail) and the foreign keys between
 *  them (Query Builder auto-join). */
export async function getSchema(): Promise<SchemaResult> {
  const res = await api.get<{ success: boolean; data: SchemaResult }>(`${BASE}/schema`);
  return res.data?.data ?? { tables: [], foreignKeys: [] };
}

/* ── saved queries (shared, server-side store) ── */

export interface SavedQuery {
  oid: string;
  name: string;
  description?: string | null;
  category?: string | null;
  sqlText: string;
  createdBy?: string | null;
  createdAt: string;
}

export interface SavedQueryInput {
  name: string;
  description?: string | null;
  category?: string | null;
  sqlText: string;
}

/** Every saved query — shared across all admin/service users. */
export async function listSaved(): Promise<SavedQuery[]> {
  const res = await api.get<{ success: boolean; data: SavedQuery[] }>(`${BASE}/saved`);
  return res.data?.data ?? [];
}

/** Save a new query; returns its new id. */
export async function createSaved(input: SavedQueryInput): Promise<string> {
  const res = await api.post<{ success: boolean; data: { oid: string } }>(`${BASE}/saved`, input);
  return res.data?.data?.oid;
}

/** Update an existing saved query. */
export async function updateSaved(oid: string, input: SavedQueryInput): Promise<void> {
  await api.put(`${BASE}/saved/${oid}`, input);
}

/** Soft-delete a saved query. */
export async function deleteSaved(oid: string): Promise<void> {
  await api.delete(`${BASE}/saved/${oid}`);
}
